import { collection, DocumentChangeType, getFirestore, onSnapshot } from "firebase/firestore";
import { makeAutoObservable, runInAction } from "mobx";
import {
	IDocumentForUI,
	IEstimateDataForUI,
	IPageForUI,
	IQueuedItemToProcess,
	ITupleOutputForUI,
} from "../shared/common/EstimateEngine/EstimateEngineTypes";
import { logDebug, logError } from "../shared/common/logger";
import { getEstimaticsCurrentProjectStore, getUserStore } from "./DataStores";
import RestAPI from "../utils/restAPI";
import { appFeatures } from "../shared/common/AppFeatures";
import EngineUtils from "../shared/common/EngineUtils";
import { toastNotify } from "../shared/client/FrontendUtils";
import { MutuallyExcludedLineItems } from "../shared/client/MutuallyExcludedLineItems";
import EngineSharedUtils from "../shared/common/EstimateEngine/EngineSharedUtils";

class ProcessedEstimatesStoreDebug {
	constructor() {
		makeAutoObservable(this);
		this._showQAPanel = window.localStorage.getItem("showQAPanel") === "true";
	}
	private _showQAPanel: boolean = true;
	public get showQAPanel() {
		return this._showQAPanel;
	}
	public set showQAPanel(value: boolean) {
		window.localStorage.setItem("showQAPanel", value ? "true" : "false");
		this._showQAPanel = value;
	}
	private _showOnlyMappingError = false;
	public get showOnlyMappingError() {
		return this._showOnlyMappingError;
	}
	public set showOnlyMappingError(value: boolean) {
		this._showOnlyMappingError = value;
	}

	private _dontShowMappingError = false;
	public get dontShowMappingError() {
		return this._dontShowMappingError;
	}
	public set dontShowMappingError(value: boolean) {
		this._dontShowMappingError = value;
	}
	private _showOnlyLooksLikeABug = false;
	public get showOnlyLooksLikeABug() {
		return this._showOnlyLooksLikeABug;
	}
	public set showOnlyLooksLikeABug(value: boolean) {
		this._showOnlyLooksLikeABug = value;
	}
	private _dontShowLooksLikeABug = false;
	public get dontShowLooksLikeABug() {
		return this._dontShowLooksLikeABug;
	}
	public set dontShowLooksLikeABug(value: boolean) {
		this._dontShowLooksLikeABug = value;
	}

	private _dontShowApproved = false;
	public get dontShowApproved() {
		return this._dontShowApproved;
	}
	public set dontShowApproved(value: boolean) {
		this._dontShowApproved = value;
	}

	private _showOnlyApproved = false;
	public get showOnlyApproved() {
		return this._showOnlyApproved;
	}

	public set showOnlyApproved(value: boolean) {
		this._showOnlyApproved = value;
	}
}
class ProcessedEstimatesStore {
	constructor() {
		makeAutoObservable(this);
	}

	locallyMergePages(pageId0: string, pageId1: string) {
		const page0 = this._allCurrentViewPages.find((page) => EngineUtils.pageDataFromPageInfo(page).pageId === pageId0);
		if (page0) {
			page0.page.mergeWith = pageId1;
		}
	}

	// logDebug("setting allCurrentViewPages", allPages);
	public readonly prefixOrder: string[] = EngineSharedUtils.prefixOrder;
	private _showErrorResolutionPanelForTuple: { tuple: ITupleOutputForUI; pageId: string } | undefined;
	public set showErrorResolutionPanelForTuple(value: { tuple: ITupleOutputForUI; pageId: string } | undefined) {
		this._showErrorResolutionPanelForTuple = value;
	}
	public get showErrorResolutionPanelForTuple() {
		return this._showErrorResolutionPanelForTuple;
	}

	private _estimatesQueueItems: { [k: string]: IQueuedItemToProcess[] } = {};
	public get estimatesQueueItems(): { [k: string]: IQueuedItemToProcess[] } {
		return this._estimatesQueueItems;
	}
	private set estimatesQueueItems(value: { [k: string]: IQueuedItemToProcess[] }) {
		this._estimatesQueueItems = value;
	}

	private _estimates: IEstimateDataForUI[] = [];
	public get estimates(): IEstimateDataForUI[] {
		return this._estimates;
	}
	private set estimates(value: IEstimateDataForUI[]) {
		this._estimates = value;
	}

	private _fullPageImagePath: string = "";
	public get fullPageImagePath() {
		return this._fullPageImagePath;
	}
	public set fullPageImagePath(value: string) {
		this._fullPageImagePath = value;
	}

	private _blinkTupleLineIndex = -1;
	public get blinkTupleLineIndex() {
		return this._blinkTupleLineIndex;
	}
	public set blinkTupleLineIndex(value: number) {
		this._blinkTupleLineIndex = value;
		if (value >= 0) {
			const that = this;
			setTimeout(() => {
				that.blinkTupleLineIndex = -1;
			}, 1000);
		}
	}

	private _editingTupleIndex: number = -1;
	public get editingTupleIndex() {
		return this._editingTupleIndex;
	}

	private _editingLineItemIndex: number = -1;
	public get editingLineItemIndex() {
		return this._editingLineItemIndex;
	}

	private _uniqueEditId = 0;
	public get uniqueEditId() {
		return this._uniqueEditId;
	}

	private _editingLineItemTuple: ITupleOutputForUI | undefined;
	public get editingLineItemTuple(): ITupleOutputForUI | undefined {
		return this._editingLineItemTuple;
	}
	public setEditingLineItemTuple(value: ITupleOutputForUI, lineItemIndex: number, tupleIndex: number, pageId: string) {
		this._editingLineItemTuple = value;
		this._editingLineItemIndex = lineItemIndex;
		this._editingTupleIndex = tupleIndex;
		this._showAddLineItemPanelForPageId = pageId;
		this._uniqueEditId++;
	}
	private _showAddLineItemPanelForPageId = "";
	public get showAddLineItemPanelForPageId() {
		return this._showAddLineItemPanelForPageId;
	}
	public set showAddLineItemPanelForPageId(value: string) {
		this._editingLineItemTuple = undefined;
		this._editingLineItemIndex = -1;
		this._editingTupleIndex = -1;
		this._showAddLineItemPanelForPageId = value;
		this._uniqueEditId++;
	}

	private mutuallyExcludeLineItems(pageInfo: { document: IDocumentForUI; documentIndex: number; page: IPageForUI }, estimateId: string) {
		const lineItems = pageInfo.page.tuplesOutput.flatMap((tuple) => tuple.lineItems);
		const pageId = EngineUtils.pageDataFromPageInfo(pageInfo).pageId;

		pageInfo.page.tuplesOutput.forEach((tuple) => {
			tuple.lineItems.forEach((lineItem) => {
				const excludedBy = MutuallyExcludedLineItems[lineItem.lineItemId];
				if (excludedBy) {
					// If the room contains any of the Line items that exclude it, set the line item to deleted.
					if (lineItems.some((li) => excludedBy.includes(li.lineItemId))) {
						logDebug("mutually excluding: ", lineItem.lineItemId, excludedBy);
						lineItem.deleted = true;
						this.updateOrCreateLineItem(tuple.tupleId, estimateId, pageId, tuple);
					}
				}
			});
		});

		return pageInfo;
	}

	private _allCurrentViewPages: {
		page: IPageForUI;
		documentIndex: number;
		// pageIndex: number,
		document: IDocumentForUI;
	}[] = [];
	private _currentViewEstimateId = "";
	private _currentViewEstimates: IEstimateDataForUI[] = [];
	async setCurrentViewEstimateId(estimateId: string) {
		if (this._currentViewEstimateId === estimateId && this.allCurrentViewPages.length > 0) {
			return;
		}
		getEstimaticsCurrentProjectStore().showLoadingAnimation = true;
		try {
			this._currentViewEstimateId = estimateId;
			this.allCurrentViewPages = [];
			const _estimates = await RestAPI.fetchAPI("/rest/estimates/get_final_estimates", "GET", {
				estimateId,
			});
			logDebug("_estimates", _estimates);
			this._currentViewEstimates = _estimates; //this.estimates.filter((estimate) => estimate.estimateQueueItem?.estimateId === estimateId);
			const _documents = this._currentViewEstimates.map((estimate) => estimate.dataForUI);
			const allPages: {
				page: IPageForUI;
				documentIndex: number;
				// pageIndex: number,
				document: IDocumentForUI;
			}[] = [];
			if (_documents.length && _documents[0].metadata?.pageIndex) {
				_documents.sort((a, b) => (a.metadata!.pageIndex ?? 0) - (b.metadata!.pageIndex! ?? 0));
			}
			_documents.forEach((document, documentIndex) => {
				let pageInfo = {
					page: document.page,
					documentIndex,
					document,
				};
				pageInfo = this.mutuallyExcludeLineItems(pageInfo, estimateId);
				allPages.push(pageInfo);
			});

			this.allCurrentViewPages = allPages;
		} catch (err) {
			toastNotify("Error getting estimate data", true);
		}
		getEstimaticsCurrentProjectStore().showLoadingAnimation = false;
	}
	public get currentViewEstimateId() {
		return this._currentViewEstimateId;
	}
	public get currentViewEstimates() {
		return this._currentViewEstimates;
	}
	public get allCurrentViewPages() {
		return this._allCurrentViewPages;
	}
	public set allCurrentViewPages(allPages: { page: IPageForUI; documentIndex: number; document: IDocumentForUI }[]) {
		runInAction(() => {
			allPages.forEach((page, pageIndex) => {
				const freeFormFlags = page.page.tuplesOutput.filter((tuple) => tuple.isFreeFormFlag);
				const tuplesOutput = page.page.tuplesOutput
					.filter((tuple) => !tuple.deleted && !tuple.isFreeFormFlag)
					.sort((a, b) => a.originalTupleIndex! - b.originalTupleIndex! /*a.tupleId.localeCompare(b.tupleId)*/)
					.map((tuple, index) => ({
						...tuple,
						orderIndex:
							tuple.lineItems[0].orderIndex !== undefined
								? tuple.lineItems[0].orderIndex
								: index +
								  this.prefixOrder.findIndex((prefix) => tuple.tupleId.replace(/^tpa_/, "").startsWith(prefix)) * 1000, // keep this for backwards compatibility for the moment
					}))
					.sort((a, b) => a.orderIndex - b.orderIndex);

				page.page.tuplesOutput = [...freeFormFlags, ...tuplesOutput];
			});
		});
		allPages &&
			allPages.length &&
			logDebug("setting allCurrentViewPages\n", allPages[0].page.tuplesOutput.map((tuple) => tuple.tupleId).join("\n"));
		this._allCurrentViewPages = allPages;
	}

	init(organizationId: string) {
		const cancelFunction = onSnapshot(
			collection(getUserStore().getFirestoreInstance(), `organizations/${organizationId}/processQueue`),
			(snapshot) => {
				snapshot.docChanges().forEach((change) => {
					const processQueueItem: IQueuedItemToProcess = change.doc.data() as IQueuedItemToProcess;
					this._handleProcessQueueChange(change.type, processQueueItem);
				});
			},
			(error) => {
				logDebug("error getting processQueue", error);
			},
		);
	}
	private _handleProcessQueueChange(changeType: DocumentChangeType, processQueueItem: IQueuedItemToProcess) {
		const that = this;
		function removeItemFromEstimate() {
			if (that.estimatesQueueItems[processQueueItem.estimateId]) {
				that.estimatesQueueItems[processQueueItem.estimateId] = that.estimatesQueueItems[processQueueItem.estimateId].filter(
					(item) => item.itemId !== processQueueItem.itemId,
				);
				logDebug("removing processQueueItem", processQueueItem);
			}
		}
		switch (changeType) {
			case "added":
			case "modified": {
				if (processQueueItem.originalUploadedFiles?.[0]?.deleted) {
					removeItemFromEstimate();
				} else {
					const itemsArray = this.estimatesQueueItems[processQueueItem.estimateId] || [];
					const ind = itemsArray.findIndex((item) => item.itemId === processQueueItem.itemId);
					if (ind < 0) {
						this.estimatesQueueItems[processQueueItem.estimateId] = [...itemsArray, processQueueItem];
					} else {
						itemsArray[ind] = processQueueItem;
						this.estimatesQueueItems[processQueueItem.estimateId] = [...itemsArray];
					}
				}
				// logDebug("adding processQueueItem", processQueueItem);
				break;
			}
			case "removed": {
				removeItemFromEstimate();
				break;
			}
		}
		// this._fetchEstimatesIfNeeded();
	}
	async updateOrCreateLineItem(
		lineItemId: string,
		estimateId: string,
		pageId: string,
		lineItemData: ITupleOutputForUI,
		source?: string,
		metadata?: any,
	) {
		const pageIndex = this._allCurrentViewPages.findIndex((pageInfo) => {
			const pageData = EngineUtils.pageDataFromPageInfo(pageInfo);
			// if (item.extractedImages?.[0]?.dataForUIPath === "creates_by_images") {
			// 	_pageId = item.extractedImages?.[0]?.pageId!;
			// }
			if (pageData.pageId === pageId) {
				return true;
			}
		});
		try {
			if (pageIndex >= 0) {
				await RestAPI.fetchAPI("/rest/estimates/updateOrCreateLineItem", "POST", {
					lineItemId,
					estimateId,
					pageId,
					lineItemData,
					source: source ?? "",
					metadata: metadata ?? {},
				});
				runInAction(() => {
					const found = this._allCurrentViewPages[pageIndex].page.tuplesOutput.some((tuple, index) => {
						if (tuple.tupleId === lineItemId) {
							this._allCurrentViewPages[pageIndex].page.tuplesOutput[index] = lineItemData;
							return true;
						}
					});
					if (!found && lineItemId.includes("$user_")) {
						// TODO :: this will add all the user's line items to the end of the list. We should sort them by the line item category
						lineItemData.originalTupleIndex = 2000000 + this._allCurrentViewPages[pageIndex].page.tuplesOutput.length;
						this.blinkTupleLineIndex = lineItemData.originalTupleIndex;
						logDebug("adding user line item with index", lineItemData.originalTupleIndex);
						this._allCurrentViewPages[pageIndex].page.tuplesOutput.push(lineItemData);
					}
					this.allCurrentViewPages = [...this._allCurrentViewPages];
				});
				// logDebug(
				// 	"only errors",
				// 	this._allCurrentViewPages[pageIndex].page.tuplesOutput.filter((tuple) => tuple.hasError)
				// );
			}
		} catch (err) {
			logError("Error updating line item", err);
		}
		return true;
	}
	// private async _fetchData(estimate: IQueuedItemToProcess) {
	// 	logDebug("fetching data for estimate", estimate);
	// 	// debugger;
	// 	const _estimates: IEstimateDataForUI[] = [];
	// 	for (const extractedImage of estimate.extractedImages ?? []) {
	// 		try {
	// 			if (
	// 				(extractedImage.dataForUIPath && extractedImage.dataForUIPath !== FileErrorDummyPath) ||
	// 				(extractedImage.estimatePath && extractedImage.estimatePath !== FileErrorDummyPath) // backward compatibility. TODO :: fix all in database ( or delete database) and remove from here
	// 			) {
	// 				//

	// 				const data: IDocumentForUI = await RestAPI.fetchAPI("/rest/estimates/get_estimate", "GET", {
	// 					estimateId: estimate.estimateId,
	// 					pageId: extractedImage.pageId,
	// 					estimatePath: extractedImage.dataForUIPath || extractedImage.estimatePath,
	// 					requestType: "dataForUI",
	// 				});
	// 				let metadata: IEstimateMetadata[] | undefined = undefined;
	// 				if (appFeatures.isDevMachine) {
	// 					metadata = await RestAPI.fetchAPI("/rest/estimates/get_estimate", "GET", {
	// 						estimatePath: extractedImage.estimatePath,
	// 						requestType: "devMetadata",
	// 					});
	// 				}

	// 				if (data) {
	// 					data.item = estimate; // TODO :: need to remove this
	// 					_estimates.push({
	// 						metadata,
	// 						estimateQueueItem: estimate,
	// 						extractedImage,
	// 						// TODO :: we should move this to backend and send only the processed data
	// 						// for now I want to the freedom to play with all the data in the frontend so I'm keeping it all
	// 						dataForUI: data,
	// 					});
	// 				} else {
	// 					this._fetchingEstimatesSet.delete(estimate.estimateId);
	// 					logError("Error fetching data for estimate", data);
	// 				}
	// 			}
	// 		} catch (err) {
	// 			logError("Error fetching data for estimate", err);
	// 		}
	// 	}
	// 	this.estimates = [...this.estimates, ..._estimates];
	// }

	// private _fetchEstimatesIfNeeded() {
	// 	Object.values(this.estimatesQueueItems).forEach((queueItems) => {
	// 		queueItems.forEach((queueItem) => {
	// 			// TODO :: monitor start process time and refetch if it's been modified ( in case of reprocessing request )
	// 			if (this._fetchingEstimatesSet.has(queueItem.itemId)) {
	// 				return;
	// 			}
	// 			if (queueItem.status === 100) {
	// 				this._fetchingEstimatesSet.add(queueItem.itemId);
	// 				this._fetchData(queueItem); // no need to wait, let it run in background
	// 			}
	// 		});
	// 	});
	// }

	public getQueueItemsForEstimateId(estimateId: string) {
		return this.estimatesQueueItems[estimateId] ?? [];
	}

	public getParentQueueItemForEstimateId(estimateId: string) {
		return this.estimatesQueueItems[estimateId].find((item) => item.isParent) ?? null;
	}
}

const _processedEstimatesStoreDebug: ProcessedEstimatesStoreDebug = new ProcessedEstimatesStoreDebug();
const getProcessedEstimatesStoreDebug = () => _processedEstimatesStoreDebug;
const _processedEstimatesStore: ProcessedEstimatesStore = new ProcessedEstimatesStore();
const getProcessedEstimatesStore = () => _processedEstimatesStore;

export { getProcessedEstimatesStore, getProcessedEstimatesStoreDebug };
